import { BehaviorSubject } from 'rxjs';
import { IPlatformRequestBody } from '../sources/requestPlatformData';
import { IExtensionRequestBody, IExtensionResponse } from '../sources/requestExtensionData';
import { IPictureOptions } from '../sources/takePicture';
import { IPlatformModalOptions } from '../actions/showModal';
import { IDict } from './utility';
import { Change, LoadingOptions, Verb } from 'q2-tecton-common/lib/types/config';
import { PartialRecord } from 'q2-tecton-common/lib/types/record';
import { ScrollData } from '../sources/platformScrollChanged';
import { INavigateOptions } from '../actions/navigateTo';
import { IOverpanelOptions } from '../actions/showOverpanel';

export { IOverpanelOptions };

export interface TectonResponse {
    response: any;
    status: number;
    statusText: string;
}

export interface ModuleProvidedTab {
    tabLabel: string;
    provided: true;
    url: '';
    moduleId: '';
    initialParams: {};
}

export interface TabInfo {
    url: string;
    moduleId: string;
    initialParams: IDict<any>;
}

export interface TectonResolvedTab extends TabInfo {
    tabLabel: string;
    provided: false;
    minHeight?: string;
    tabLabelOverride?: string;
    formPostAuth: boolean;
    loadingOptions?: LoadingOptions;
    requiredUserCapabilities?: string[] | undefined;
}

export interface IContactRecord {
    email: string[];
    name: string;
    phone: string[];
}

export interface OpenAppArgs {
    iOSScheme?: string;
    iOSAppStoreUrl?: string;
    errMsgTitle?: string;
    errMsgText?: string;
    androidPackage?: string;
    androidParamString?: string;
    redirectToAppStore?: boolean;
}

export type ResolvedTab = TectonResolvedTab | ModuleProvidedTab;

export interface TectonResolvedMenuItem {
    itemLabel: string;
    action: string;
    moduleId: string;
}

export enum PlatformAlertType {
    Info = 'info',
    Error = 'error',
    Warning = 'warning',
    Success = 'success'
}

export enum LogLevels {
    'debug',
    'info',
    'warning',
    'error'
}

export type LogLevelStrings = keyof typeof LogLevels;

export interface IPlatformAlertOptions<Type extends PlatformAlertType = PlatformAlertType> {
    msg?: string;
    message: string;
    styleType: Type;
    closable?: boolean;
    durationInMs?: number;
    onClickNavigation?: IAlertNaviationViaOverpanelOptions | IPlatformNavigationOptions | void;
}

export interface ParamOptions {
    replace: boolean;
    type: 'string' | 'number' | 'boolean';
}

export interface IBasePlatformNavigationOptions {
    moduleName: string;
    moduleOptions: IDict<ParamOptions>;
}

export interface IAlertNaviationViaOverpanelOptions extends IBasePlatformNavigationOptions {
    asOverpanel: true;
}

export interface IPlatformNavigationOptions extends IBasePlatformNavigationOptions {
    asOverpanel: false;
}

export interface IActions {
    showOverpanel?<T>(
        overpanelPath: string,
        params?: IDict<string>,
        options?: IOverpanelOptions
    ): Promise<T>;
    enrollPush?(nickname: string): Promise<{ token: string; nickname: string }>;
    unenrollPush?(): Promise<void>;
    closeOverpanel?(resolutionData?: IDict<any>): void;
    shareText?(text: string, title?: string): Promise<void>;
    showModal?(modalOptions: IPlatformModalOptions): Promise<string>;
    showAlert?(alertOptions: IPlatformAlertOptions): void;
    navigateTo?(featureName: string): void;
    navigateTo?(options: INavigateOptions): void;
    navigateTo?(featureName: string, moduleName: string): void;
    navigateTo?(featureName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, queryParams: IDict<any>): void;
    navigateTo?(featureName: string, urlParams: any[], queryParams: IDict<any>): void;
    navigateTo?(featureName: string, moduleName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, moduleName: string, queryParams: IDict<any>): void;
    navigateTo?(
        featureName: string,
        moduleName: string,
        urlParams: any[],
        queryParams: IDict<any>
    ): void;
    scrollToTop?(): void;
    manageChanges?(changeData: PartialRecord<Verb, Change[]>): Promise<XMLHttpRequest>;
    setTitle?(title: string): void;
    setParams?(params: IDict<string>): void;
    clearParams?(): void;
    notifyRefetchAccounts?(): void;
    printWindow?(): void;
    keepAlive?(): void;
    showLoadingModal?(title?: string): void;
    clearLoadingModal?(): void;
    setFetching(fetching?: boolean): void;
    logToServer?(message: string, level?: LogLevelStrings): void;
    openURL?(
        url: string,
        options: {
            showInOverpanel?: boolean;
            nativeBrowserIfInApp?: boolean;
            requiresToken?: boolean;
        }
    ): void;
    openApp?(params: OpenAppArgs): void;
    openModule?(route: string, data?: string): Promise<void>;
    resizeIframe(): void;
    clearCache?(...routePrefixes: string[]): Promise<void>;
    actionRequired?(uuid: string, title: string, message?: string): void;
    removeActionRequired?(uuid: string): void;
}

export interface IActionsStubs {
    closeOverpanel?(resolutionData?: IDict<any>): void;
    enrollPush?(nickname: string): Promise<{ token: string; nickname: string }>;
    unenrollPush?(): Promise<void>;
    shareText?(text: string, title?: string): Promise<void>;
    showModal?<B1, B2, B3, TransRow>(modalOptions: IPlatformModalOptions): Promise<string>;
    showAlert?(alertOptions: IPlatformAlertOptions): void;
    navigateTo?(featureName: string): void;
    navigateTo?(options: INavigateOptions): void;
    navigateTo?(featureName: string, moduleName: string): void;
    navigateTo?(featureName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, queryParams: IDict<any>): void;
    navigateTo?(featureName: string, urlParams: any[], queryParams: IDict<any>): void;
    navigateTo?(featureName: string, moduleName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, moduleName: string, queryParams: IDict<any>): void;
    navigateTo?(
        featureName: string,
        moduleName: string,
        urlParams: any[],
        queryParams: IDict<any>
    ): void;
    manageChanges?(changeData: PartialRecord<Verb, Change[]>): Promise<XMLHttpRequest>;
    setTitle?(title: string): void;
    setParams?(params: IDict<string>): void;
    clearParams?(): void;
    notifyRefetchAccounts?(): void;
    printWindow?(): void;
    keepAlive?(): void;
    showLoadingModal?(title?: string): void;
    clearLoadingModal?(): void;
    setFetching?(fetching?: boolean): void;
    openURL?(
        url: string,
        options: {
            showInOverpanel?: boolean;
            nativeBrowserIfInApp?: boolean;
            requiresToken?: boolean;
        }
    ): void;
    openApp?(params: OpenAppArgs): void;
    openModule?(route: string, data?: string): Promise<void>;
    resizeIframe?(): void;
    clearCache?(...routePrefixes: string[]): Promise<void>;
}

export interface ISources {
    cacheCleared?(route: string, callback: () => void): () => void;
    canUser?(...capabilities: string[]): Promise<boolean>;
    getPushEnrollment?(): Promise<{ token: string; nickname: string }>;
    getCurrentLanguageCode(): string;
    getFeatureConfig?(): Promise<IDict<any>>;
    getPlatformInfo?(...requestedPlatformInfo: string[]): Promise<IDict<any>>;
    getQRCode?(title: string): Promise<string>;
    getQRDeviceId?(): Promise<{ deviceId: string; deviceType: string }>;
    getAuthToken?(): Promise<string>;
    fetchPlatformData?(route: string): Promise<TectonResponse>;
    isNavigable?(featureName: string, moduleName?: string): Promise<boolean>;
    languageChanged?(callback: (language?: string) => void): void;
    loc(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    paramsChanged(callback: (params?: IDict<string>) => void): () => void;
    pickContact?(): Promise<IContactRecord>;
    promptForMFA?(promptOptions?: { message?: string }): Promise<void>;
    platformScrollChanged?(callback: (scrollData: ScrollData & ClientRect) => void): () => void;
    refetchRequired?(dataType: string, callback: () => void): () => void;
    requestExtensionData?<R>(
        requestOptions?: IExtensionRequestBody
    ): Promise<IExtensionResponse<R>>;
    requestOutletInfo?(): Promise<OutletInfo>;
    requestPlatformData?(requestOptions?: IPlatformRequestBody): Promise<TectonResponse>;
    takePicture?(pictureOptions?: IPictureOptions): Promise<string>;
    getTectonCacheObject?(key: string, moduleId?: string): Promise<object>;
    setTectonCacheObject?(key: string, data: object): Promise<void>;
    getUserConfigByKey?(key: string, namespace?: string): Promise<any>;
    getUserConfig?(namespace?: string): Promise<IDict<any>>;
    setUserConfig?(key: string, value: any, namespace?: string): Promise<void>;
    removeUserConfig?(key: string, namespace?: string): Promise<void>;
}

export interface ISourcesStubs {
    cacheCleared?(route: string, callback: () => void): () => void;
    canUser?(...capabilities: string[]): Promise<boolean>;
    fetchPlatformData?(route: string): Promise<TectonResponse>;
    checkPushEnrollment?(): Promise<{ token: string; nickname: string }>;
    getCurrentLanguageCode?(): string;
    getFeatureConfig?(): Promise<IDict<any>>;
    getPlatformInfo?(...requestedPlatformInfo: string[]): Promise<IDict<any>>;
    getQRCode?(title: string): Promise<string>;
    getQRDeviceId?(): Promise<{ deviceId: string; deviceType: string }>;
    getAuthToken?(): Promise<string>;
    isNavigable?(featureName: string, moduleName?: string): Promise<boolean>;
    languageChanged?(callback: () => void): () => void;
    loc?(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    paramsChanged?(callback: (params?: IDict<string>) => void): () => void;
    pickContact?(): Promise<IContactRecord>;
    platformScrollChanged?(callback: (scrollData: ScrollData & ClientRect) => void): () => void;
    promptForMFA?(promptOptions?: { message?: string }): Promise<void>;
    refetchRequired?(dataType: string, callback: () => void): () => void;
    requestExtensionData?<R>(
        requestOptions?: IExtensionRequestBody
    ): Promise<IExtensionResponse<R>>;
    requestOutletInfo?(): Promise<OutletInfo>;
    requestPlatformData?(requestOptions?: IPlatformRequestBody): Promise<TectonResponse>;
    takePicture?(pictureOptions?: IPictureOptions): Promise<string>;
    getTectonCacheObject?(key: string, moduleId?: string): Promise<object>;
    setTectonCacheObject?(key: string, data: object): Promise<void>;
}

export interface IPlatformCoreAPI {
    actions?: IActions;
    outletContext?: string;
    params$?: BehaviorSubject<IDict<string>>;
    sources?: ISources;
    teardown(): void;
}

export interface IConnectOptions {
    baseLanguagePath?: string;
    legacy?: boolean;
    moduleId?: string;
    testOptions?: {
        actions?: IActionsStubs;
        sources?: ISourcesStubs;
        outletContext?: string;
        initialParams?: IDict<any>;
        loadElements?: boolean;
    };
}

export type TectonWindow = typeof window & {
    TECTONSDKVERSION: string;
    Tecton: {
        PLATFORM_VERSION: string;
        FI_IDENTIFIER: string;
        TECTON_SDK_VERSION: string;
        TECTON_PLATFORM_VERSION: string;
        UUX_VERSION?: string;
        inMobileApp?: boolean;
        pendo?: {
            apiKey?: string;
            additionalApiKeys?: string[];
        };
        connect?(options: IConnectOptions): Promise<IPlatformCoreAPI>;
    };
};

export interface OutletInfo {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
    x: number;
    y: number;
}
